<template>
  <div
    v-if="loading"
    class="absolute inset-0 flex items-center justify-center bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm z-10"
  >
    <LoadingComponent />
  </div>
  <div :key="refreshKey" class="flex flex-col gap-5">
    <!-- Wrapper -->
    <div
      class="relative flex flex-col w-full text-main-700 bg-white shadow-xl rounded-xl p-6 gap-6 dark:bg-gray-800 dark:text-gray-200"
    >
      <!-- Header -->
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5 mb-6 portrait:items-start items-center"
      >
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-start justify-start portrait:items-start landscape:mr-2"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 ease-in-out transform hover:scale-105"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
        </div>
        <button
          @click="handleOpenVideo"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
        >
          <i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i
          ><span>Watch Video Tutorial</span>
        </button>
      </nav>

      <!-- Hero Section -->
      <section class="text-center py-10">
        <h1
          class="text-3xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100"
        >
          Buy Domain For Your Store!
        </h1>
        <p
          class="mt-4 max-w-2xl mx-auto text-lg text-gray-600 dark:text-gray-300"
        >
          Search and register the perfect domain for our Store.
        </p>

        <!-- Search -->
        <div
          class="mt-8 flex w-full max-w-2xl mx-auto rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden"
        >
          <input
            v-model="searchQuery"
            @keyup.enter="searchDomains"
            type="text"
            placeholder="Search domain..."
            class="flex-grow px-4 py-3 text-gray-800 dark:bg-gray-700 dark:text-gray-200 focus:outline-none"
          />
          <button
            @click="searchDomains"
            class="px-6 py-3 bg-button text-white font-medium hover:opacity-90 dark:bg-sub-800 dark:hover:bg-sub-900 transition"
          >
            <i class="fas fa-search mr-2"></i> Search
          </button>
        </div>

        <!-- Popular TLDs -->
        <div class="mt-6">
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Popular store extensions:
          </p>
          <div class="flex flex-wrap justify-center gap-2">
            <span
              v-for="tld in popularTlds"
              :key="tld"
              @click="addTld(tld)"
              class="px-4 py-2 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 cursor-pointer dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition"
            >
              .{{ tld }}
            </span>
          </div>
        </div>
      </section>

      <section v-if="searchPerformed">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-semibold">Domain Availability</h2>
        </div>

        <!-- Domain List -->
        <div class="space-y-4">
          <div
            v-for="domain in filteredDomains"
            :key="domain.Domain"
            class="flex flex-col md:flex-row justify-between items-start md:items-center p-6 bg-white dark:bg-gray-800 border border-main-100 dark:border-sub-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
          >
            <!-- Info -->
            <div class="mb-4 md:mb-0">
              <div class="flex items-center flex-wrap gap-2">
                <span
                  class="text-xl font-semibold text-main-900 dark:text-white"
                >
                  {{ domain.Domain }}
                </span>

                <!-- Status Badge -->
                <span
                  v-if="domain.Available"
                  class="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                >
                  Available
                </span>
                <span
                  v-else
                  class="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                >
                  Taken
                </span>

                <!-- Premium Badge -->
                <span
                  v-if="domain.IsPremiumName === 'true'"
                  class="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  Premium
                </span>
              </div>

              <div class="mt-2 text-sm text-main-500 dark:text-sub-300">
                Registered: {{ domain.Available ? "No" : "Yes" }} • Premium:
                {{ domain.IsPremiumName === "true" ? "Yes" : "No" }}
              </div>
            </div>

            <!-- Actions -->
            <div
              class="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto"
            >
              <span
                v-if="domain.Available"
                class="text-xl font-bold text-main-800 dark:text-white"
              >
                ${{ domain.price }}/yr
              </span>
              <span
                v-else
                class="text-xl font-bold text-gray-400 dark:text-gray-500"
              >
                Unavailable
              </span>

              <button
                class="px-5 py-2.5 rounded-xl font-medium transition-all duration-200 shadow-sm"
                :class="
                  domain.Available
                    ? 'bg-main-600 dark:bg-sub-800 hover:bg-main-700 hover:dark:bg-sub-900 text-white hover:shadow'
                    : 'bg-gray-200  text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
                "
                :disabled="!domain.Available"
                @click="buyDomain(domain)"
              >
                {{ domain.Available ? "Buy" : "Unavailable" }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
export default {
  name: "DomainSearch",
  data() {
    return {
      searchQuery: "",
      searchPerformed: false,
      loading: false,
      cartOpen: false,
      filter: "all",
      showNotification: false,
      popularTlds: ["com", "shop", "store", "ng", "in", "io", "co", "online"],
      availableDomains: [],
      takenDomains: [],
      cart: [],
      domains: [],
      refreshKey: 0, // For forcing re-render
    };
  },
  computed: {
    filteredDomains() {
      if (this.filter === "available") {
        return this.domains.filter((d) => d.Available);
      } else if (this.filter === "taken") {
        return this.domains.filter((d) => !d.Available);
      }
      return this.domains; // "all"
    },
  },
  methods: {
    ...mapActions(["searchDomain", "buyDomain"]),
    async searchDomains() {
      if (!this.searchQuery.trim()) return;
      this.loading = true;
      this.searchPerformed = true;
      let queries = null;

      this.searchQuery = this.searchQuery.trim().toLowerCase();

      // Check if searchQuerys already has a tld attached
      const hasTld = this.popularTlds.some((tld) =>
        this.searchQuery.endsWith(`.${tld}`)
      );

      if (hasTld) {
        queries = this.searchQuery;
      } else {
        queries = this.popularTlds
          .map((tld) => `${this.searchQuery}.${tld}`)
          .join(",");
      }

      try {
        const response = await this.searchDomain({ query: queries });

        if (response.success) {
          this.domains = response.data;
        } else {
          toast.info(
            response.error || "Domain search failed. Please try again."
          );
          this.availableDomains = [];
          this.takenDomains = [];
        }
      } catch (e) {
        console.error("Domain search error:", e);
      } finally {
        this.loading = false;
      }
    },
    addTld(tld) {
      this.searchQuery += "." + tld;
      this.searchDomains();
    },
    async buyDomain(domain) {
      this.loading = true;
      try {
        const response = await this.buyDomain({
          domain: domain.Domain,
          years: 1,
        });
        console.log("This is the response", response);

        if (response.success) {
          toast.success(
            `Purchase ${domain.Domain} ${
              response.data.length && response.data[0].Registered === "true"
                ? "successful!"
                : "failed!"
            }`
          );
          this.searchDomains(); // Refresh the domain list
        } else {
          toast.error(
            response.error || "Domain purchase failed. Please try again."
          );
        }
      } catch (e) {
        console.error("Domain purchase error:", e);
        toast.error("An error occurred during purchase. Please try again.");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.domain-card {
  transition: all 0.3s ease;
}
.domain-card:hover {
  transform: translateY(-5px);
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
.cart-slide-in {
  animation: slideIn 0.5s forwards;
}
@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
</style>
